import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge } from '@wit-support/ui';
import { ContentNetwork, } from '@wit-support/admin-contracts';
import { CONTENT_NETWORK_LABEL_RU } from './content-labels.js';
import { NetworkChip } from './content-network-chip.js';
import { cn } from './lib/cn.js';
/**
 * Расписание публикаций движка (только чтение): у аккаунта — времена, дни
 * недели, сети автопубликации и длина ролика по сетям. Три вида строк:
 * основной короткий ролик (все сети), дополнительный ролик одной сети
 * (второй TikTok за день) и длинный ролик. Источник — `GET /api/content/schedule`
 * (конфиг расписания движка).
 */
/** Дни недели движка: 0 — воскресенье … 6 — суббота; показ — с понедельника. */
const DAY_SHORT_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
/** «каждый день» / «по будням» / «по выходным» / «пн, ср, вс». */
export function formatScheduleDaysRu(days) {
    const set = new Set(days.filter((d) => d >= 0 && d <= 6));
    if (set.size === 0)
        return 'дни не заданы';
    if (set.size === 7)
        return 'каждый день';
    const same = (xs) => xs.length === set.size && xs.every((d) => set.has(d));
    if (same([1, 2, 3, 4, 5]))
        return 'по будням';
    if (same([0, 6]))
        return 'по выходным';
    return WEEK_ORDER.filter((d) => set.has(d))
        .map((d) => DAY_SHORT_RU[d])
        .join(', ');
}
const ZONE_RU = {
    'America/Los_Angeles': 'Лос-Анджелес',
    'Europe/Moscow': 'Москва',
    UTC: 'UTC',
};
/** IANA-зона расписания словами (знакомые — по-русски, прочие — как есть). */
export function contentScheduleZoneRu(tz) {
    return ZONE_RU[tz] ?? tz;
}
// ---------------------------------------------------------------------------
// Длина ролика по сетям: [минимум, максимум] секунд из конфига
// ---------------------------------------------------------------------------
/** Секунды диапазона: целые — как есть, дробные — с запятой («17,5»). */
function secRu(n) {
    return Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ',');
}
/** «15–20 с»; одинаковые края — «30 с». */
export function formatDurationRangeRu([min, max]) {
    return min === max ? `${secRu(min)} с` : `${secRu(min)}–${secRu(max)} с`;
}
/**
 * Сети с одинаковым диапазоном — одной группой (в порядке сетей движка);
 * группы — в порядке первой сети. Нет диапазонов — `[]`.
 */
export function groupDurationRanges(durations) {
    if (!durations)
        return [];
    const groups = new Map();
    for (const n of ContentNetwork.options) {
        const r = durations[n];
        if (!r)
            continue;
        const key = `${r[0]}|${r[1]}`;
        const g = groups.get(key) ?? { range: [r[0], r[1]], networks: [] };
        g.networks.push(n);
        groups.set(key, g);
    }
    return [...groups.values()];
}
/**
 * «YouTube 15–20 с · Instagram, TikTok 15–30 с»; нет диапазонов — null.
 * `omitSingleNetworks` — строка одной сети (её имя уже рядом): одна группа
 * показывается без имён сетей — «15–30 с».
 */
export function formatDurationRangesRu(durations, { omitSingleNetworks = false } = {}) {
    const groups = groupDurationRanges(durations);
    if (groups.length === 0)
        return null;
    if (omitSingleNetworks && groups.length === 1)
        return formatDurationRangeRu(groups[0].range);
    return groups
        .map((g) => `${g.networks.map((n) => CONTENT_NETWORK_LABEL_RU[n]).join(', ')} ${formatDurationRangeRu(g.range)}`)
        .join(' · ');
}
const durationsKey = (d) => groupDurationRanges(d)
    .map((g) => `${g.networks.join('+')}=${g.range.join('-')}`)
    .join(';');
const rowKey = (i) => [
    i.kind === 'long' ? 'long' : i.extra ? 'extra' : 'short',
    [...i.days].sort().join(','),
    i.networks === 'all' ? 'all' : [...i.networks].sort().join(','),
    durationsKey(i.durationSec),
].join('|');
/** Сети `wide` шире `narrow` и покрывают их целиком (`all` покрывает всё). */
function covers(wide, narrow) {
    if (wide === 'all')
        return true;
    return wide.length > narrow.length && narrow.every((n) => wide.includes(n));
}
const rowKind = (i) => i.kind === 'long' ? 'long' : i.extra ? 'extra' : 'short';
/**
 * ЗАПАСНОЙ путь для старого движка, который признак `extra` не шлёт.
 * Дополнительный слот сети: короткая строка с частью сетей, когда у того же
 * аккаунта есть короткая строка с более широким набором (основной слот —
 * «все сети»). До нового расписания у аккаунта один набор сетей на все
 * времена — такие строки не помечаются. Вызывается, только если ни одна
 * строка расписания не несёт явного `extra`.
 */
function markExtra(rows) {
    const shorts = rows.filter((r) => r.kind === 'short');
    return rows.map((r) => {
        if (r.kind !== 'short' || r.networks === 'all' || r.networks.length === 0)
            return r;
        const narrow = r.networks;
        return shorts.some((o) => o !== r && covers(o.networks, narrow)) ? { ...r, kind: 'extra' } : r;
    });
}
const KIND_ORDER = { short: 0, extra: 1, long: 2 };
/**
 * Строки движка (аккаунт × время) → аккаунты; времена с одинаковым видом,
 * днями, сетями и длиной — одной строкой, по возрастанию. Внутри аккаунта:
 * основной слот, дополнительные, длинные. Порядок аккаунтов — как у движка.
 */
export function groupContentSchedule(items) {
    const out = new Map();
    // новый движок говорит про доп. слот сам; угадываем только у старого
    const explicit = items.some((i) => i.extra);
    for (const i of items) {
        const acc = out.get(i.account) ?? { enabled: i.enabled, rows: new Map() };
        acc.enabled = acc.enabled && i.enabled;
        const k = rowKey(i);
        const row = acc.rows.get(k) ?? {
            times: [],
            days: [...i.days],
            networks: i.networks,
            kind: rowKind(i),
            durationSec: i.durationSec,
        };
        if (!row.times.includes(i.time))
            row.times.push(i.time);
        acc.rows.set(k, row);
        out.set(i.account, acc);
    }
    return [...out].map(([account, a]) => ({
        account,
        enabled: a.enabled,
        rows: (() => {
            const rows = [...a.rows.values()].map((r) => ({ ...r, times: [...r.times].sort() }));
            return (explicit ? rows : markExtra(rows)).sort((x, y) => KIND_ORDER[x.kind] - KIND_ORDER[y.kind] ||
                (x.times[0] ?? '').localeCompare(y.times[0] ?? ''));
        })(),
    }));
}
function Networks({ networks }) {
    if (networks === 'all')
        return _jsx("span", { children: "\u0432\u0441\u0435 \u0441\u0435\u0442\u0438" });
    if (networks.length === 0) {
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u0430\u0432\u0442\u043E\u043F\u0443\u0431\u043B\u0438\u043A\u0430\u0446\u0438\u044F \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D\u0430" });
    }
    return (_jsx("span", { className: "inline-flex flex-wrap gap-1", children: networks.map((n) => (_jsx(NetworkChip, { network: n }, n))) }));
}
/** Бейдж вида строки: основной слот без бейджа. */
export const CONTENT_SCHEDULE_ROW_KIND_LABEL_RU = {
    short: null,
    extra: 'ещё один ролик за день',
    long: 'длинный',
};
const KIND_HINT_RU = {
    short: null,
    extra: 'Дополнительный короткий ролик только в этой сети — в тот же день, в другое время',
    long: 'Длинный ролик из самых сильных клипов аккаунта',
};
/** «длина: YouTube 15–20 с · Instagram, TikTok 15–30 с». */
function Durations({ row }) {
    const text = formatDurationRangesRu(row.durationSec, {
        omitSingleNetworks: row.networks !== 'all' && row.networks.length === 1,
    });
    if (text === null)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u00B7" }), _jsxs("span", { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u0434\u043B\u0438\u043D\u0430: " }), text] })] }));
}
/**
 * Строки одного аккаунта: «09:00 · каждый день · все сети · длина: …»,
 * «17:48 · каждый день · TikTok [ещё один ролик за день]», «[длинный] 14:48 · ср, вс …».
 */
export function ContentScheduleAccountRows({ account, className, }) {
    return (_jsx("ul", { className: cn('flex flex-col gap-1 text-sm', className), children: account.rows.map((r) => {
            const badge = CONTENT_SCHEDULE_ROW_KIND_LABEL_RU[r.kind];
            return (_jsxs("li", { "data-schedule-kind": r.kind, className: "flex flex-wrap items-center gap-x-2 gap-y-1", children: [badge ? (_jsx(Badge, { variant: r.kind === 'long' ? 'info' : 'neutral', title: KIND_HINT_RU[r.kind] ?? undefined, children: badge })) : null, _jsx("span", { className: "font-medium tabular-nums", children: r.times.join(', ') }), _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u00B7" }), _jsx("span", { children: formatScheduleDaysRu(r.days) }), _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u00B7" }), _jsx(Networks, { networks: r.networks }), _jsx(Durations, { row: r })] }, `${r.kind}-${r.times.join()}-${r.days.join()}`));
        }) }));
}
/** Подпись под расписанием, если где-то заданы диапазоны длины. */
export const CONTENT_SCHEDULE_DURATION_NOTE_RU = 'Длина — ориентир: ролик набирается целыми клипами, клипы не обрезаются. Если для сети задан более короткий диапазон, туда уходит укороченная версия того же ролика — первые клипы.';
/** Расписание всех аккаунтов: аккаунт, его времена, дни, сети и длина роликов. */
export function ContentScheduleSummary({ timezone, items, className, }) {
    const accounts = groupContentSchedule(items);
    if (accounts.length === 0) {
        return _jsx("p", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0414\u0432\u0438\u0436\u043E\u043A \u043D\u0435 \u043E\u0442\u0434\u0430\u043B \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u0435." });
    }
    return (_jsxs("div", { className: cn('flex flex-col gap-3', className), children: [_jsx("ul", { className: "flex flex-col divide-y divide-[var(--color-border-subtle)]", children: accounts.map((a) => (_jsxs("li", { className: "flex flex-col gap-1 py-2 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:gap-4", children: [_jsxs("span", { className: "flex min-w-40 items-center gap-2 font-medium break-all", children: [a.account, a.enabled ? null : _jsx(Badge, { variant: "neutral", children: "\u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D" })] }), _jsx(ContentScheduleAccountRows, { account: a, className: "min-w-0 flex-1" })] }, a.account))) }), timezone ? (_jsxs("p", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0412\u0440\u0435\u043C\u044F \u2014 ", contentScheduleZoneRu(timezone), "."] })) : null, accounts.some((a) => a.rows.some((r) => r.durationSec !== null)) ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: CONTENT_SCHEDULE_DURATION_NOTE_RU })) : null] }));
}
//# sourceMappingURL=content-schedule.js.map